import { ncsToHex } from '../libs/ncs-converter.js';
import A11yDialog from 'a11y-dialog';
import '../utils/ios-detector.js';

class CustomColorPicker {
    constructor() {
        this.init();
    }

    init() {
        // Create the dialog container if it doesn't exist
        this.container = document.querySelector('#color-picker-container');
        if (!this.container) {
            console.error('Color picker container not found');
            return;
        }

        // Remove any hidden attribute that might interfere with a11y-dialog
        if (this.container.hasAttribute('hidden')) {
            this.container.removeAttribute('hidden');
        }

        // Make sure aria-hidden is set correctly
        this.container.setAttribute('aria-hidden', 'true');

        // Initialize the a11y dialog
        this.dialog = new A11yDialog(this.container);

        // Debug - check if dialog was created successfully

        // Get DOM elements
        this.form = this.container.querySelector('form');
        if (!this.form) {
            console.error('Form not found in dialog');
            return;
        }

        this.ncsInput = this.container.querySelector('#color-ncs');
        if (!this.ncsInput) {
            console.error('NCS input not found in dialog');
            return;
        }

        this.submitButton = this.form.querySelector('button[type="submit"]');
        this.colorPreviews = document.querySelectorAll('[data-color-preview]');
        this.colorTexts = document.querySelectorAll('[data-custom-color-text]');
        this.hideWhenColorSelected = document.querySelectorAll('[data-hide-when-color-selected]');

        // Print all active open color trigger buttons
        const openTriggers = document.querySelectorAll('[data-use-custom-color]');

        // Setup event listeners
        this.addOpenEventListeners();
        this.addCloseEventListeners();
        this.addFormEventListeners();
        this.addEnterKeyListener();
        this.addDialogEventListeners();

        // Initialize NCS validation
        this.handleNcsChange();
    }

    // Add event listeners for dialog events
    addDialogEventListeners() {
        // Listen for dialog opening
        this.dialog.on('show', () => {
            document.body.classList.add('dialog-open');
        });

        // Listen for dialog closing
        this.dialog.on('hide', () => {
            document.body.classList.remove('dialog-open');
        });
    }

    addEnterKeyListener() {
        this.ncsInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (this.valid) {
                    this.form.dispatchEvent(new Event('submit'));
                }
            }
        });
    }

    addFormEventListeners() {
        this.form.addEventListener('submit', (e) => {
            this.handleSubmit(e);
        });

        this.ncsInput.addEventListener('input', () => {
            this.handleNcsChange();
        });
    }

    addOpenEventListeners() {
        document.querySelectorAll('[data-use-custom-color]').forEach((element) => {
            element.addEventListener('click', (e) => {
                this.target = e.target;
                e.preventDefault();

                // Debug to ensure we're capturing click events

                try {
                    // Show the dialog
                    this.dialog.show();
                } catch (error) {
                    console.error('Error showing dialog:', error);
                }

                // Delay focus to work better on mobile
                setTimeout(() => {
                    if (this.ncsInput) {
                        this.ncsInput.focus();
                    }
                }, 100);
            });
        });
    }

    addCloseEventListeners() {
        document.querySelectorAll('[data-close]').forEach((element) => {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                this.dialog.hide();
            });
        });
    }

    handleSubmit(e) {
        e.preventDefault();

        if (!this.valid) {
            return;
        }

        window.customColor = this.ncsColor;

        this.colorTexts.forEach((text) => {
            text.textContent = this.ncsColor;
        });

        this.target.checked = true;
        this.target.dispatchEvent(new Event('input', { bubbles: true }));
        this.target.dispatchEvent(new Event('change', { bubbles: true }));

        this.dialog.hide();

        // Dispatch custom event for other components that might need it
        document.dispatchEvent(
            new CustomEvent('custom-color-selected', {
                detail: {
                    ncs: this.ncsColor,
                    hex: this.hexColor
                }
            })
        );
    }

    handleNcsChange() {
        const ncs = this.ncsInput.value;

        this.ncsColor = ncs.toUpperCase();
        this.hexColor = ncsToHex(this.ncsColor);

        this.valid = this.hexColor ? true : false;

        this.updateUI();
    }

    updateUI() {
        if (this.valid) {
            this.colorPreviews.forEach((preview) => {
                preview.style.backgroundColor = this.hexColor;
            });
        } else {
            this.colorPreviews.forEach((preview) => {
                preview.style.backgroundColor = '';
            });
        }

        this.submitButton.disabled = !this.valid;

        this.hideWhenColorSelected.forEach((element) => {
            element.classList.toggle('js-hidden', this.valid);
        });
    }
}

// Initialize the color picker when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new CustomColorPicker();
});

export default CustomColorPicker;
